import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Plus } from 'lucide-react';
import { Button, cn } from '@scio3/ui';

/**
 * Blurred facsimile of the portal, rendered BEHIND the lapsed-account paywall in place of the real
 * route.
 *
 * Why a facsimile instead of the live page: the paywall deliberately floats over the portal rather
 * than replacing it, so a lapsed user sees their account is still there ("your stuff is right
 * here, just pay") instead of a dialog on a grey void. Doing that with the REAL pages meant the
 * backend had to allow a lapsed account to read `groups` / `assets` / `playlists` / `schedules` —
 * four full list APIs, whose resolvers accept a caller-supplied `first`, so anyone who deleted the
 * paywall overlay in devtools could page through the whole account. Rendering a facsimile removes
 * the reason those APIs were ever allowlisted: nothing behind the paywall queries content, so the
 * allowlist drops to the aggregate count query the dialog needs.
 *
 * The COUNTS in the paywall stay real (`getContentCounts`) — "24 files are safe" only reassures
 * because it is true, and a made-up number is a lie in the one moment we are asking for a card.
 * Only the backdrop is synthetic, and it is inert and `aria-hidden`.
 *
 * PER-ROUTE LAYOUT. The paywall lifts on the routes a lapsed user is allowed to use, so it can
 * appear over any of the four content pages — and they do not share a shape. Rendering the Screens
 * layout everywhere made the other three obviously wrong: their side rails simply were not there.
 * The rail widths below are read off the real pages; keep them in step:
 *   - Screens    (`screens/pages/ScreensPage.tsx`)   — no rail, full-width card
 *   - Files      (`assets/pages/AssetsPage.tsx`)     — 280px left rail (`lg:block`), card grid
 *   - Playlists  (`playlists/pages/PlaylistsPage.tsx`) — 280px left + 300px right (`xl`) rails
 *   - Schedules  (`schedules/pages/SchedulesPage.tsx`) — 280px left rail, calendar body
 *
 * WHERE THE BLUR GOES — the one structural subtlety. It sits on an inner wrapper around the
 * content, NOT on the outer container, so the page background and each card's rounded edge stay
 * crisp and only the "data" is unreadable. Blurring the outer element softens the card borders
 * into a glow and reads as a rendering fault. And never `scale-*` to hide the blur's soft edge: a
 * transform scales from the center, so it pushes the toolbar off the left edge and clips the
 * buttons off the top-right — the page looks zoomed rather than out of focus. Clipping is handled
 * by `overflow-hidden` instead, which costs nothing.
 *
 * Placeholder BARS, not invented names. Under the blur a grey bar and blurred text are
 * indistinguishable, and bars avoid two problems real-looking strings would create: a pile of new
 * i18n keys shipped to nine languages for decoration nobody can read, and synthetic content that
 * could be mistaken for the user's own if the blur ever failed to apply.
 *
 * Sibling: `survey/components/OnboardingBackdropToolbar.tsx` does the same job behind the
 * onboarding survey (Screens only, unblurred). If the Screens toolbar gains a row, both are wrong
 * together.
 */

/**
 * How far out of focus the backdrop sits. **Knob 1 of 2** — edit the px and Tailwind rescans on
 * save, so HMR picks it up with no restart. Must stay a literal class string, not a template, or
 * the JIT never emits the rule.
 *
 * The tradeoff, because lower is not automatically nicer: the content here is placeholder bars, and
 * blur is what disguises them. Wind it down and the bars sharpen into uniform rounded rectangles at
 * a repeating pitch — precisely the visual grammar of a LOADING SKELETON. So less blur reads as
 * "still loading", not as "your content, softened".
 *
 * What buys headroom is VARIANCE, not blur: the bars vary in height and a few carry the primary
 * tint, because real content is uneven and a skeleton is not. That is what lets 4px hold where a
 * uniform version needed 5-6px.
 *
 *   6px+  — mush; the silhouette survives but the panels stop being distinguishable
 *   4px   — current. Unreadable, still obviously a populated page
 *   2-3px — bar edges resolve; goes skeleton-ish on the grid and calendar first
 *   0-1px — plainly fake
 */
const BACKDROP_BLUR = 'blur-[4px]';

/**
 * Ink weights. **Knob 2 of 2** — everything grey in here draws from this map, so "too dark" / "too
 * washed out" is one edit rather than thirty.
 *
 * Deliberately light. These sit on `bg-card` under a blur, and blur does not lighten — it spreads.
 * So weights that look right unblurred come out as a heavy grey slab once the filter applies, which
 * is what "too dark" was. The whole surface should read as a page you cannot focus on, not as a
 * page dimmed behind a scrim; the paywall dialog is what owns the visual weight here, and the
 * backdrop must not compete with it.
 *
 * MONOCHROME on purpose. An earlier version tinted a minority of elements (online pills, some
 * thumbnails) with the primary green, on the theory that a little colour breaks up the skeleton
 * read. It did not survive looking at it: scattered green blocks behind a blur do not resemble
 * anything on the real page, so they read as random decoration rather than content — and they
 * compete with the dialog's own green CTA a few hundred pixels away. Variance comes from WEIGHT and
 * SIZE only. If this ever needs colour again, it has to map to something the real page actually
 * shows in colour at that position, not just "some cells are green".
 */
const TONE = {
  /** Chrome placeholders: toolbar controls, search, rail rows. The lightest weight. */
  faint: 'bg-muted-foreground/8',
  /** Default content bar. */
  base: 'bg-muted-foreground/12',
  /** The "name" column — the one thing that should read as primary text. */
  strong: 'bg-muted-foreground/15',
  /** Checkbox outline. */
  outline: 'border-muted-foreground/15',
} as const;

type BodyKind = 'list' | 'grid' | 'playlist' | 'calendar';

type Layout = {
  /** 280px left rail — the folder tree on Files, the entity list on Playlists / Schedules. */
  leftRail: boolean;
  /** 300px right rail — only Playlists, whose third panel is the asset picker. */
  rightRail: boolean;
  body: BodyKind;
};

const SCREENS_LAYOUT: Layout = { leftRail: false, rightRail: false, body: 'list' };

/**
 * Matched against `location.pathname` by substring, in order. Paths mirror the `/app/*` routes in
 * `routes/index.tsx` (kept as scio1's URLs). Anything unmatched falls back to Screens, which is
 * both the most common landing route and where the paywall redirects.
 */
const ROUTE_LAYOUTS: ReadonlyArray<readonly [string, Layout]> = [
  ['/app/assetManagement', { leftRail: true, rightRail: false, body: 'grid' }],
  ['/app/playlistManagement', { leftRail: true, rightRail: true, body: 'playlist' }],
  ['/app/scheduleManagement', { leftRail: true, rightRail: false, body: 'calendar' }],
  ['/app/screenManagement', SCREENS_LAYOUT],
];

/**
 * Bar widths per row. Varied deliberately: identical bars read as a loading skeleton, and this is
 * not a loading state — it is meant to read as a populated page. Long enough to overflow any
 * realistic viewport height, because the list must reach the bottom of the card; a short list
 * leaves a slab of empty grey under it that immediately gives the facsimile away.
 */
const ROW_WIDTHS = [
  { name: 'w-40', content: 'w-32' },
  { name: 'w-28', content: 'w-24' },
  { name: 'w-48', content: 'w-28' },
  { name: 'w-32', content: 'w-36' },
  { name: 'w-44', content: 'w-24' },
  { name: 'w-24', content: 'w-32' },
  { name: 'w-36', content: 'w-28' },
  { name: 'w-40', content: 'w-20' },
  { name: 'w-32', content: 'w-28' },
  { name: 'w-44', content: 'w-36' },
  { name: 'w-28', content: 'w-24' },
  { name: 'w-48', content: 'w-32' },
  { name: 'w-36', content: 'w-20' },
  { name: 'w-40', content: 'w-28' },
  { name: 'w-24', content: 'w-36' },
  { name: 'w-44', content: 'w-24' },
  { name: 'w-32', content: 'w-32' },
  { name: 'w-40', content: 'w-28' },
] as const;

/** Generic grey block — every placeholder in here is one of these. */
function Bar({ className }: { className?: string }) {
  return <div className={cn('rounded', TONE.base, className)} />;
}

/** A rail card: the folder tree / entity list panel that flanks three of the four pages. */
function Rail({ width }: { width: string }) {
  return (
    <div className={cn('hidden shrink-0 overflow-hidden pl-4 lg:block', width)}>
      <div className="flex h-full flex-col gap-2 overflow-hidden rounded-xl bg-card p-3">
        <Bar className={cn('h-8 w-full', TONE.faint)} />
        <div className="mt-1 flex flex-col gap-3">
          {['w-32', 'w-40', 'w-24', 'w-36', 'w-28', 'w-44', 'w-32', 'w-24', 'w-40', 'w-28'].map(
            (w, i) => (
              <div key={`${w}-${i}`} className="flex items-center gap-2">
                <Bar className={cn('h-4 w-4 shrink-0', TONE.faint)} />
                <Bar className={cn('h-4', w)} />
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
}

/** Screens / Schedules list rows — proportions mirror `list/ScreenRow.tsx`. */
function ListBody() {
  return (
    <div className="min-h-0 flex-1 overflow-hidden border-t border-border">
      {ROW_WIDTHS.map((row, i) => (
        <div
          key={`${row.name}-${i}`}
          className="flex items-center gap-2 border-b border-border px-3 py-3"
        >
          <div className="flex w-[60px] shrink-0 items-center justify-center">
            <div className={cn('h-4 w-4 rounded border', TONE.outline)} />
          </div>
          {/* Status pill — the real page's Offline/Online badge. Rounded-full and narrow so the
              blurred silhouette is a pill, not another bar. Every third row takes a
              heavier weight: a column of identical pills is the strongest skeleton tell, and a
              real account is a mix of online and offline. */}
          <div className="flex w-16 shrink-0 items-center">
            <div className={cn('h-5 w-14 rounded-full', i % 3 === 0 ? TONE.strong : TONE.base)} />
          </div>
          {/* Heights alternate by a hair — a fixed pitch reads mechanical. */}
          <Bar className={cn(i % 2 ? 'h-4' : 'h-3.5', TONE.strong, row.name)} />
          <Bar className={cn('ml-auto shrink-0', i % 3 === 1 ? 'h-3.5' : 'h-4', row.content)} />
        </div>
      ))}
    </div>
  );
}

/** Files/Assets — a card grid, not rows. Thumbnail block over a two-line caption. */
function GridBody() {
  return (
    <div className="min-h-0 flex-1 overflow-hidden border-t border-border p-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {Array.from({ length: 20 }, (_, i) => (
          <div key={i} className="overflow-hidden rounded-lg border border-border">
            {/* Thumbnail weight alternates. A grid of identical blocks is the most
                skeleton-like of the four surfaces, so this one needs the variance most. */}
            <Bar
              className={cn('h-24 w-full rounded-none', i % 4 === 0 ? TONE.strong : TONE.base)}
            />
            <div className="flex flex-col gap-1.5 p-2">
              <Bar
                className={cn(
                  'h-3',
                  TONE.strong,
                  i % 3 === 0 ? 'w-3/4' : i % 3 === 1 ? 'w-1/2' : 'w-2/3',
                )}
              />
              <Bar className={cn('h-2.5 w-1/3', TONE.faint)} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Playlists centre panel — the item editor: a drag handle, thumbnail, name, duration. */
function PlaylistBody() {
  return (
    <div className="min-h-0 flex-1 overflow-hidden border-t border-border">
      {ROW_WIDTHS.slice(0, 14).map((row, i) => (
        <div
          key={`${row.name}-${i}`}
          className="flex items-center gap-3 border-b border-border px-3 py-2.5"
        >
          <Bar className={cn('h-4 w-3 shrink-0', TONE.faint)} />
          <Bar className={cn('h-10 w-16 shrink-0', i % 3 === 0 ? TONE.strong : TONE.base)} />
          <Bar className={cn('h-4', TONE.strong, row.name)} />
          <Bar className={cn('ml-auto h-4 w-12 shrink-0', TONE.faint)} />
        </div>
      ))}
    </div>
  );
}

/** Schedules — a month grid rather than a list. */
function CalendarBody() {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border p-3">
      <div className="mb-2 grid grid-cols-7 gap-2">
        {Array.from({ length: 7 }, (_, i) => (
          <Bar key={i} className={cn('h-3 w-10', TONE.faint)} />
        ))}
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-7 grid-rows-5 gap-2">
        {Array.from({ length: 35 }, (_, i) => (
          <div key={i} className="flex flex-col gap-1 rounded-md border border-border p-1.5">
            <Bar className={cn('h-2.5 w-4', TONE.faint)} />
            {/* Sparse event chips — a calendar with something in every cell reads as noise. Mixed
                weights so the month does not look stamped. */}
            {i % 3 === 0 && <Bar className={cn('h-3 w-full', TONE.strong)} />}
            {i % 5 === 0 && <Bar className={cn('h-3 w-2/3', TONE.strong)} />}
            {i % 7 === 0 && <Bar className={cn('h-3 w-1/2', TONE.base)} />}
          </div>
        ))}
      </div>
    </div>
  );
}

const BODIES: Record<BodyKind, () => React.JSX.Element> = {
  list: ListBody,
  grid: GridBody,
  playlist: PlaylistBody,
  calendar: CalendarBody,
};

export function LapsedPortalBackdrop() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const layout =
    ROUTE_LAYOUTS.find(([prefix]) => pathname.startsWith(prefix))?.[1] ?? SCREENS_LAYOUT;
  const Body = BODIES[layout.body];

  return (
    <div
      aria-hidden="true"
      data-testid="lapsed-portal-backdrop"
      data-layout={layout.body}
      className="pointer-events-none flex h-full overflow-hidden bg-muted pb-2 select-none"
    >
      {/* Rails are OUTSIDE the blur wrapper's card but inside the same blurred layer, so the whole
          page goes out of focus as one image rather than panel-by-panel. */}
      <div className={cn('flex min-h-0 w-full flex-1 overflow-hidden', BACKDROP_BLUR)}>
        {layout.leftRail && <Rail width="w-[280px]" />}

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-2">
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-xl bg-card">
            {/* Row 1 — folder title + primary actions. Mirrors the real pages' silhouette; inert. */}
            <div className="flex items-center justify-between gap-2 px-3 pt-5 pb-2.5">
              <span className="text-base font-medium text-foreground">{t('Home')}</span>
              <div className="flex shrink-0 items-center gap-2">
                <Button tabIndex={-1}>
                  <Plus className="h-4 w-4" />
                  {t('Add Screen')}
                </Button>
                <Bar className={cn('h-9 w-24 rounded-md', TONE.faint)} />
                <Bar className={cn('h-9 w-28 rounded-md', TONE.faint)} />
              </div>
            </div>

            {/* Row 2 — search + filter/sort/view controls as placeholder blocks. Always shown: a
                lapsed account has by definition had screens, so the Screens page's "hide controls
                until first screen" case cannot apply. */}
            <div className="flex items-center gap-2 px-3 pt-1 pb-2.5">
              <Bar className={cn('h-8 min-w-0 max-w-xs flex-1 rounded-md', TONE.faint)} />
              <div className="ml-auto flex items-center gap-1">
                <Bar className={cn('h-8 w-24 rounded-md', TONE.faint)} />
                <Bar className={cn('h-8 w-20 rounded-md', TONE.faint)} />
                <Bar className={cn('h-8 w-20 rounded-md', TONE.faint)} />
                <Bar className={cn('h-8 w-16 rounded-md', TONE.faint)} />
              </div>
            </div>

            <Body />
          </div>
        </div>

        {layout.rightRail && <Rail width="w-[300px]" />}
      </div>
    </div>
  );
}
